import type { WebsiteContent } from '../types/content';

const STORAGE_KEY = 'vote-site:content-cache';
const STORAGE_VERSION = 2;

export interface ContentCacheEntry {
  data: WebsiteContent;
  fetchedAt: number;
  etag: string | null;
  lastModified: string | null;
  contentSignature: string;
}

interface StoredContentCache {
  version: number;
  fetchedAt: number;
  etag: string | null;
  lastModified: string | null;
  contentSignature: string;
  data: WebsiteContent;
}

let memoryEntry: ContentCacheEntry | null = null;

export function getContentSignature(data: WebsiteContent): string {
  return `${data.schemaVersion}\u0000${data.createdAt}`;
}

function isFresh(fetchedAt: number, maxAgeMs: number): boolean {
  return fetchedAt > 0 && Date.now() - fetchedAt < maxAgeMs;
}

function validateContent(data: WebsiteContent): boolean {
  return Boolean(data.highLevelIssues?.length && data.index?.byId);
}

function toEntry(parsed: StoredContentCache): ContentCacheEntry | null {
  if (parsed.version !== STORAGE_VERSION || !validateContent(parsed.data)) {
    return null;
  }

  return {
    data: parsed.data,
    fetchedAt: parsed.fetchedAt,
    etag: parsed.etag,
    lastModified: parsed.lastModified,
    contentSignature: parsed.contentSignature,
  };
}

function readStoredEntry(): ContentCacheEntry | null {
  if (memoryEntry && validateContent(memoryEntry.data)) {
    return memoryEntry;
  }

  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredContentCache;
    const entry = toEntry(parsed);
    if (!entry) return null;

    memoryEntry = entry;
    return entry;
  } catch {
    return null;
  }
}

/** Returns cached content only while inside the client TTL window. */
export function readFreshContentCache(maxAgeMs: number): WebsiteContent | null {
  const entry = readStoredEntry();
  if (!entry || !isFresh(entry.fetchedAt, maxAgeMs)) return null;
  return entry.data;
}

/** Returns cached content and validators regardless of age. */
export function readContentCacheEntry(): ContentCacheEntry | null {
  return readStoredEntry();
}

export function isContentCacheFresh(entry: ContentCacheEntry, maxAgeMs: number): boolean {
  return isFresh(entry.fetchedAt, maxAgeMs);
}

export function writeContentCache(
  data: WebsiteContent,
  validators?: { etag: string | null; lastModified: string | null },
): void {
  const fetchedAt = Date.now();
  const entry: ContentCacheEntry = {
    data,
    fetchedAt,
    etag: validators?.etag ?? null,
    lastModified: validators?.lastModified ?? null,
    contentSignature: getContentSignature(data),
  };

  memoryEntry = entry;

  if (typeof window === 'undefined') return;

  try {
    const payload: StoredContentCache = {
      version: STORAGE_VERSION,
      fetchedAt: entry.fetchedAt,
      etag: entry.etag,
      lastModified: entry.lastModified,
      contentSignature: entry.contentSignature,
      data,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota or private mode: in-memory cache still applies for this session.
  }
}

/** Extends TTL after server confirms content is unchanged. */
export function touchContentCache(validators?: {
  etag: string | null;
  lastModified: string | null;
}): void {
  const entry = readStoredEntry();
  if (!entry) return;

  writeContentCache(entry.data, {
    etag: validators?.etag ?? entry.etag,
    lastModified: validators?.lastModified ?? entry.lastModified,
  });
}

export function clearContentCache(): void {
  memoryEntry = null;

  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function readResponseValidators(res: Response): {
  etag: string | null;
  lastModified: string | null;
} {
  return {
    etag: res.headers.get('etag'),
    lastModified: res.headers.get('last-modified'),
  };
}

export function buildConditionalRequestHeaders(
  entry: ContentCacheEntry,
): HeadersInit | undefined {
  const headers: Record<string, string> = {};
  if (entry.etag) headers['If-None-Match'] = entry.etag;
  if (entry.lastModified) headers['If-Modified-Since'] = entry.lastModified;
  return Object.keys(headers).length > 0 ? headers : undefined;
}
